import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Divider,
  Fade,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import {
  displayNameFromAlgorithm,
  displayNameFromProperty,
} from '../../../domain/friendly-properties';
import {
  GeometricAlgorithmType,
  isGeometricAlgorithmType,
} from '../../../domain/geometry/algorithm-types';
import {
  GeometricProperty,
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import {
  PropertyContext,
  resolveDynamicQuantity,
} from '../../../domain/property-operations';
import { ResolvedQuantity } from '../../../domain/property-types';
import {
  CalculationType,
  CalculationTypeInput,
  DatabaseLookupTypeInput,
  ElementIdentifierDbidType,
  PropertySetType,
  QuantityOperation,
  UnitSettingsType,
} from '../../../gql/graphql';
import { useGeometricAlgorithms } from '../../../hooks/geometric-algorithms';
import { useViewer } from '../../common/ForgeViewer';
import PlaceholderText from '../../text/PlaceholderText';
import { formatQuantity } from '../../../utils/quantity-utils';

type DynamicQuantitiesModalProps = {
  allowSumOnly?: boolean; // For now we only allow either SUM or SUM and EXTRACT_VALUES, so make some simplifications
  propertyContext: PropertyContext;
  propertySets: PropertySetType[];
  isOpen: boolean;
  dbIds: ElementIdentifierDbidType[];
  defaultCalculation: CalculationType | undefined | null;
  onClose: () => void;
  onLink: (
    calculation: CalculationTypeInput | null,
    quantity: ResolvedQuantity | null,
    unitFormatOverride?: UnitSettingsType,
    databaseLookup?: DatabaseLookupTypeInput
  ) => void;
  onUnlink: () => void;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
};

type LinkedCountFormData = {
  selectedSource: CalculationSource.LINKED_COUNT;
};

// Removed separate Information/Quantities/Location sources; use Property cascader instead
type SparkelCalculationsFormData = {
  selectedSource: CalculationSource.PROPERTIES_CALCULATIONS;
  selectedAlgorithm: GeometricAlgorithmType | null;
  sumValues: boolean;
};

type BIMPropertyFormData = {
  selectedSource: CalculationSource.BIM_PROPERTY;
  selectedAttributeName: string | null;
  selectedCategory: string | null;
  sumValues: boolean;
};

type DynamicQuantitiesModalFormData =
  | LinkedCountFormData
  | SparkelCalculationsFormData
  | BIMPropertyFormData;

enum CalculationSource {
  PROPERTIES_CALCULATIONS = 'Calculations',
  BIM_PROPERTY = 'Property',
  LINKED_COUNT = 'Linked count',
}

const getPropertiesOptions = (
  translate: (key: string) => string = (key) => key
) =>
  Object.values(GeometricProperty).map((property) => ({
    label: displayNameFromProperty(property, translate),
    value: property,
  }));

const sourceOptions = [
  CalculationSource.BIM_PROPERTY,
  CalculationSource.PROPERTIES_CALCULATIONS,
  CalculationSource.LINKED_COUNT,
].map((source) => ({
  label: source,
  value: source,
}));

// Jump through some hoops to map from existing schema for now although it does not align with the form any more
// eslint-disable-next-line complexity
const mapCalculationTypeToFormData = (
  defaultCalculation: CalculationType | null | undefined,
  allowSumOnly: boolean | undefined
): DynamicQuantitiesModalFormData => {
  const defaultCategory = defaultCalculation?.attribute?.category;
  const defaultAttributeName = defaultCalculation?.attribute?.name;
  if (defaultCalculation?.operation === QuantityOperation.Count) {
    return { selectedSource: CalculationSource.LINKED_COUNT };
  }
  const sumValues =
    allowSumOnly || defaultCalculation?.operation === QuantityOperation.Sum;

  if (defaultCategory === SPARKEL_GEOMETRIC_PROPERTY_SET) {
    if (isGeometricAlgorithmType(defaultAttributeName ?? '')) {
      return {
        selectedSource: CalculationSource.PROPERTIES_CALCULATIONS,
        selectedAlgorithm: (defaultAttributeName ??
          null) as GeometricAlgorithmType | null,
        sumValues,
      };
    } else {
      return {
        selectedSource: CalculationSource.BIM_PROPERTY,
        selectedCategory: defaultCategory ?? null,
        selectedAttributeName: defaultAttributeName ?? null,
        sumValues,
      };
    }
  } else if (defaultCategory === SPARKEL_LOCATION_PROPERTY_SET) {
    return {
      selectedSource: CalculationSource.BIM_PROPERTY,
      selectedCategory: defaultCategory ?? null,
      selectedAttributeName: defaultAttributeName ?? null,
      sumValues,
    };
  } else {
    return {
      selectedSource: CalculationSource.BIM_PROPERTY,
      selectedCategory: defaultCategory ?? null,
      selectedAttributeName: defaultAttributeName ?? null,
      sumValues,
    };
  }
};

const mapFormDataToCalculationType = (
  formValues: DynamicQuantitiesModalFormData
): CalculationTypeInput => {
  const operation =
    formValues.selectedSource === CalculationSource.LINKED_COUNT
      ? QuantityOperation.Count
      : formValues.sumValues
      ? QuantityOperation.Sum
      : QuantityOperation.Extract;
  if (formValues.selectedSource === CalculationSource.LINKED_COUNT) {
    return {
      operation,
    };
  } else if (
    formValues.selectedSource === CalculationSource.PROPERTIES_CALCULATIONS
  ) {
    return {
      operation,
      attribute: {
        category: SPARKEL_GEOMETRIC_PROPERTY_SET,
        name: formValues.selectedAlgorithm as string,
      },
    };
  } else {
    invariant(formValues.selectedCategory, 'Category is not set');
    invariant(formValues.selectedAttributeName, 'Attribute is not set');
    return {
      operation,
      attribute: {
        category: formValues.selectedCategory,
        name: formValues.selectedAttributeName,
      },
    };
  }
};

// eslint-disable-next-line complexity
export default function DynamicQuantitiesModal({
  propertyContext,
  allowSumOnly,
  propertySets,
  dbIds,
  isOpen,
  defaultCalculation,
  onClose,
  onLink,
  onUnlink,
  projectSettings,
}: DynamicQuantitiesModalProps): React.ReactElement {
  const { t } = useTranslation('common');

  const algorithms = useGeometricAlgorithms();
  const calculationOptions = algorithms.map((algorithm) => ({
    label: displayNameFromAlgorithm(algorithm, t),
    value: algorithm,
  }));

  const { loadedModels, isAllModelsLoaded } = useViewer();

  const [dynamicQuantity, setDynamicQuantity] =
    useState<ResolvedQuantity | null>(null);
  const [isResolvingQuantities, setResolvingQuantities] = useState(false);

  const defaultValues = mapCalculationTypeToFormData(
    defaultCalculation,
    allowSumOnly
  );

  const { watch, handleSubmit, reset, register, setValue, control } =
    useForm<DynamicQuantitiesModalFormData>({
      defaultValues,
    });

  const selectedSource = watch('selectedSource');
  const selectedAttributeName = watch('selectedAttributeName');
  const selectedCategory = watch('selectedCategory');
  const selectedAlgorithm = (watch('selectedAlgorithm') ??
    null) as GeometricAlgorithmType | null;
  const sumValues = watch('sumValues');

  useEffect(() => {
    const resolve = async () => {
      let calculation: CalculationTypeInput | null = null;
      try {
        const formDataMap = {
          [CalculationSource.LINKED_COUNT]: (): LinkedCountFormData => ({
            selectedSource: CalculationSource.LINKED_COUNT,
          }),
          [CalculationSource.PROPERTIES_CALCULATIONS]:
            (): SparkelCalculationsFormData => ({
              selectedSource: CalculationSource.PROPERTIES_CALCULATIONS,
              selectedAlgorithm,
              sumValues,
            }),
          [CalculationSource.BIM_PROPERTY]: (): BIMPropertyFormData => ({
            selectedSource: CalculationSource.BIM_PROPERTY,
            selectedCategory,
            selectedAttributeName,
            sumValues,
          }),
        };

        const formData = formDataMap[selectedSource]();
        calculation = mapFormDataToCalculationType(formData);
      } catch (e) {
        // Form is not complete and we cannot resolve values
        setDynamicQuantity(null);
        return;
      }

      if (
        !isNil(calculation) &&
        !isNil(calculation?.attribute?.name) &&
        isAllModelsLoaded &&
        Object.keys(loadedModels).length > 0 &&
        dbIds.some((dbId) => dbId.dbIds.length > 0)
      ) {
        try {
          setResolvingQuantities(true);
          const quantity = await resolveDynamicQuantity(
            propertyContext,
            dbIds,
            calculation,
            projectSettings
          );
          setDynamicQuantity(quantity);
        } catch (error) {
          console.error('Error in resolving', error);
        } finally {
          setResolvingQuantities(false);
        }
      } else {
        setDynamicQuantity(null);
      }
    };
    resolve();
  }, [
    dbIds,
    loadedModels,
    isAllModelsLoaded,
    selectedAlgorithm,
    selectedCategory,
    selectedAttributeName,
    sumValues,
    selectedSource,
    propertyContext,
    projectSettings,
  ]);

  const propertiesByCategory: { [category: string]: string[] } =
    Object.fromEntries(
      propertySets.map((set) => [set.category, set.properties])
    );

  const categoryOptions = Object.keys(propertiesByCategory).map((key) => ({
    label: key,
    value: key,
  }));

  const attributeOptions =
    selectedCategory && selectedCategory in propertiesByCategory
      ? propertiesByCategory[selectedCategory].map((property) => ({
          label: property,
          value: property,
        }))
      : [];

  const onSubmitInternal = handleSubmit((values) => {
    const calculation = mapFormDataToCalculationType(values);
    onLink(calculation, dynamicQuantity);
    onClose();
  });

  const onUnlinkInternal = () => {
    onUnlink();
    reset();
    onClose();
  };

  const onCloseInternal = () => {
    reset();
    onClose();
  };

  const value = dynamicQuantity
    ? formatQuantity(dynamicQuantity, undefined, false)
    : null;

  const selectStyle = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    menuList: (provided: any) => ({
      ...provided,
      borderRadius: 'md',
    }),
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseInternal} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Dynamic value</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          <form onSubmit={onSubmitInternal} id="link-quantities-form">
            <Flex direction={'column'} gap={2}>
              <FormControl isRequired>
                <FormLabel>Source</FormLabel>
                <Controller
                  control={control}
                  name="selectedSource"
                  render={({ field: { onChange, value, ...rest } }) => (
                    <Select
                      {...rest}
                      chakraStyles={selectStyle}
                      aria-label="Select value source"
                      value={{ label: value, value: value }}
                      autoFocus
                      onChange={(e) => {
                        invariant(e);
                        onChange(e.value);
                        setValue('selectedAttributeName', null);
                        setValue('selectedCategory', null);
                        setValue('selectedAlgorithm', null);
                      }}
                      options={sourceOptions}
                    />
                  )}
                />
              </FormControl>
              {selectedSource === CalculationSource.BIM_PROPERTY ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Property set</FormLabel>
                    <Controller
                      control={control}
                      name="selectedCategory"
                      rules={{ required: 'Please select a category' }}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          {...rest}
                          chakraStyles={selectStyle}
                          value={value ? { label: value, value: value } : null}
                          autoFocus
                          onChange={(e) => {
                            onChange(e?.value);
                            setValue('selectedAttributeName', null);
                          }}
                          options={categoryOptions}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Property</FormLabel>
                    <Controller
                      control={control}
                      name="selectedAttributeName"
                      rules={{ required: 'Please select a property' }}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          {...rest}
                          chakraStyles={selectStyle}
                          value={value ? { label: value, value: value } : null}
                          onChange={(e) => onChange(e?.value)}
                          options={attributeOptions}
                        />
                      )}
                    />
                  </FormControl>
                </>
              ) : null}
              {selectedSource === CalculationSource.PROPERTIES_CALCULATIONS ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Calculations</FormLabel>
                    <Controller
                      control={control}
                      name="selectedAlgorithm"
                      rules={{ required: 'Please select a property' }}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          {...rest}
                          chakraStyles={selectStyle}
                          aria-label="Select a calculation type"
                          isDisabled={isResolvingQuantities}
                          value={
                            value
                              ? {
                                  label: displayNameFromAlgorithm(
                                    value as GeometricAlgorithmType,
                                    t
                                  ),
                                  value: value,
                                }
                              : null
                          }
                          onChange={(e) => onChange(e?.value)}
                          options={calculationOptions}
                        />
                      )}
                    />
                  </FormControl>
                </>
              ) : null}
              {selectedSource !== CalculationSource.LINKED_COUNT ? (
                <Checkbox {...register('sumValues')} disabled={allowSumOnly}>
                  Sum values
                </Checkbox>
              ) : null}
              <Text
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
              >
                {value && !isResolvingQuantities ? (
                  <>
                    <span color="gray.400">Value</span> <samp>{value}</samp>
                  </>
                ) : null}
              </Text>
              {isResolvingQuantities && (
                <Fade in={isResolvingQuantities} delay={{ enter: 1 }}>
                  <Box>
                    <PlaceholderText as="span">
                      Resolving quantities, please wait...
                    </PlaceholderText>{' '}
                    <Spinner size="sm" color="brand.400" />
                  </Box>
                </Fade>
              )}
              <DynamicQuantityDebugView
                dynamicQuantity={dynamicQuantity}
                isResolvingQuantities={isResolvingQuantities}
              />
            </Flex>
          </form>
        </ModalBody>
        <Divider orientation="horizontal" mt={1} />
        <ModalFooter gap={2}>
          <Button colorScheme="brand" type="submit" form="link-quantities-form">
            Link
          </Button>
          {defaultCalculation ? (
            <Button
              colorScheme="red"
              variant="outline"
              type="reset"
              onClick={onUnlinkInternal}
            >
              Unlink
            </Button>
          ) : null}
          <Button colorScheme="gray" onClick={onCloseInternal}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
/* eslint-enable complexity */

// Base64 decodes the model name and pulls out the last part of the name
function formatModelName(modelName: string): string {
  const decoded = decodeURIComponent(atob(modelName));
  const lastPart = decoded.split('/').pop();
  return lastPart ?? decoded;
}
const DynamicQuantityDebugView = ({
  dynamicQuantity,
  isResolvingQuantities,
}: {
  dynamicQuantity: ResolvedQuantity | null;
  isResolvingQuantities: boolean;
}) => {
  const enableDebugView = useFlag('enable-quantity-debug-view');

  if (
    !enableDebugView ||
    !isResolvingQuantities ||
    !dynamicQuantity?.dataPoints
  ) {
    return null;
  }

  return (
    <>
      <Divider orientation="horizontal" mt={8} />
      <Accordion allowToggle>
        <AccordionItem>
          <Heading as="h2">
            <AccordionButton>
              Debug <AccordionIcon />
            </AccordionButton>
          </Heading>
          <AccordionPanel px={4}>
            <TableContainer overflowY="scroll" maxHeight="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th isNumeric>DBID</Th>
                    <Th isNumeric>Quantity</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dynamicQuantity.dataPoints.map((dataPoint) => (
                    <Tr key={`${dataPoint.modelUrn}-${dataPoint.dbId}`}>
                      <Td>{formatModelName(dataPoint.modelUrn)}</Td>
                      <Td isNumeric>{dataPoint.dbId}</Td>
                      <Td isNumeric>{dataPoint.value}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
};
